import { FC, useCallback, useEffect, useState } from 'react'
import { Button } from '@cargomart/ui'
import { useNavigate } from '@tanstack/react-router'
import { MODAL_IDS, ModalItem, ModalSimple, Seo, useModal } from 'components'
import { useDebounce, useIsMobile, useOnMount } from 'hooks'
import { useGetContactListQuery } from 'modules/contact/api'
import {
  useAttachContactByCustomerIdMutation,
  useDeleteCustomerMutation,
  useDetachContactByCustomerIdMutation,
  useGetContactsByCustomerIdQuery,
  useGetCustomerByIdQuery,
  useGetCustomerListQuery
} from 'modules/customer/api'
import { CustomerContactsTable } from 'modules/customer/components'
import { PlaceholderCell } from 'src/tailwind-components/extends/PlaceholderCell'

import { customerDetailRoute, customerRoute } from '../../routes'

export const CustomerDetail: FC = () => {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { id } = customerDetailRoute.useParams()
  const { data, isLoading } = useGetCustomerByIdQuery(id || '')
  const {
    data: attachedContacts,
    isLoading: isAttachedContactsLoading,
    isFetching: isAttachedContactsFetching,
    refetch: refetchAttachedContacts
  } = useGetContactsByCustomerIdQuery(id || '')
  const [attachContactByCustomerId] = useAttachContactByCustomerIdMutation()
  const [detachContactByCustomerId] = useDetachContactByCustomerIdMutation()

  const { refetch } = useGetCustomerListQuery()
  const { openModal, closeModal } = useModal()
  const [deleteCustomer, { isLoading: isDeleting }] = useDeleteCustomerMutation()

  const title = 'контрагента'
  const subtitle = 'контрагентом'

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const debouncedSearchContact = useDebounce(debouncedSearchTerm, 500)
  const [searchQuery, setSearchQuery] = useState(debouncedSearchContact)

  const {
    data: contacts,
    isLoading: isContactListLoading,
    isFetching: isContactListFetching,
    refetch: refetchContacts
  } = useGetContactListQuery(searchQuery)

  const handleSearchContact = (search: string) => {
    setDebouncedSearchTerm(search)
    setSearchQuery(search)
  }

  useOnMount(() => {
    openModal(MODAL_IDS.DetailCustomer)
  })

  useEffect(() => {
    if (debouncedSearchContact.length === 0 || debouncedSearchContact.length > 1) {
      setSearchQuery(searchQuery)
    }
  }, [debouncedSearchContact, searchQuery])

  useEffect(() => {
    refetchAttachedContacts()
  }, [attachedContacts]) // eslint-disable-line

  const onClose = () => {
    navigate(customerRoute)
    closeModal(MODAL_IDS.DetailCustomer)
  }

  const handleDelete = useCallback(() => {
    if (data) {
      deleteCustomer(data.id).finally(() => {
        closeModal(MODAL_IDS.DeleteCustomerDetail)
        refetch()
        navigate(customerRoute)
      })
    }
  }, [closeModal, data, deleteCustomer, navigate, refetch])

  const handleOpenModalDelete = useCallback(() => {
    openModal(MODAL_IDS.DeleteCustomerDetail)
  }, [openModal])

  const modalTitle = isMobile ? (
    <div className="flex items-center gap-4 capitalize ">Контрагенты</div>
  ) : (
    data && data.name
  )

  return (
    <>
      <Seo
        description="Просмотр детальной информации о контрагенте, размещенном на сервисе QP5 Express: наименование, юридические данные."
        title="Информация о контрагенте – сборные перевозки QP5 Express"
      />
      <ModalItem id={MODAL_IDS.DeleteCustomerDetail}>
        <ModalSimple
          id={MODAL_IDS.DeleteCustomerDetail}
          onClose={() => closeModal(MODAL_IDS.DeleteCustomerDetail)}
          title={`Удалить ${title} «${data ? data.name : '-'}»?`}
          mobileModalFooter={
            <Button
              className="block w-full"
              disabled={!data?.access.delete}
              loading={isDeleting}
              onClick={handleDelete}
              size="lg"
              variant="solid"
            >
              Удалить
            </Button>
          }
          modalFooter={
            <div className="ml-auto flex gap-x-2">
              <Button
                className="ml-auto"
                onClick={() => closeModal(MODAL_IDS.DeleteCustomerDetail)}
                size="lg"
                variant="outline"
              >
                Не удалять
              </Button>
              <Button
                disabled={!data?.access.delete}
                loading={isDeleting}
                onClick={handleDelete}
                size="lg"
                variant="solid"
              >
                Удалить
              </Button>
            </div>
          }
        >
          {`В заявках, которые были ранее созданы с этим ${subtitle}, информация о нём сохранится.`}
        </ModalSimple>
      </ModalItem>

      <ModalItem id={MODAL_IDS.DetailCustomer}>
        <ModalSimple
          id={MODAL_IDS.DetailCustomer}
          onClose={onClose}
          size="nm"
          title={isLoading ? <PlaceholderCell height={32} /> : modalTitle}
          titleTestId="customer-page--customer-detail--name"
          mobileModalFooter={
            <Button
              className="block w-full"
              disabled={!data?.access.delete}
              loading={isDeleting}
              onClick={handleOpenModalDelete}
              size="lg"
              variant="solid"
            >
              Удалить
            </Button>
          }
          modalFooter={
            <div className="ml-auto flex gap-x-2">
              <Button
                disabled={!data?.access.delete}
                loading={isDeleting}
                onClick={handleOpenModalDelete}
                size="lg"
                variant="outline"
              >
                Удалить
              </Button>
              <Button className="ml-auto" onClick={onClose} size="lg" variant="solid">
                Готово
              </Button>
            </div>
          }
        >
          <div>
            {isMobile && (
              <div className="flex gap-4 pb-2 text-body-md">
                <div className="w-full max-w-[152px] text-[rgba(0,0,0,0.54)]">Название</div>
                <div className="w-full text-[rgba(0,0,0,0.72)]">
                  {isLoading ? <PlaceholderCell className="max-w-[200px]" height={20} /> : (data && data.name) || '—'}
                </div>
              </div>
            )}
            <div className="flex gap-4 pb-2 text-body-md">
              <div className="w-full max-w-[152px] text-[rgba(0,0,0,0.54)]">ИНН</div>
              <div className="w-full text-[rgba(0,0,0,0.72)]" data-test-id="customers-page--customer-detail--inn">
                {isLoading ? <PlaceholderCell className="max-w-[200px]" height={20} /> : (data && data.inn) || '—'}
              </div>
            </div>
            <div className="flex gap-4 pb-2 text-body-md">
              <div className="w-full max-w-[152px] text-[rgba(0,0,0,0.54)]">КПП</div>
              <div className="w-full text-[rgba(0,0,0,0.72)]">
                {isLoading ? <PlaceholderCell className="max-w-[200px]" height={20} /> : (data && data.kpp) || '—'}
              </div>
            </div>
            <div className="flex gap-4 pb-2 text-body-md">
              <div className="w-full max-w-[152px] text-[rgba(0,0,0,0.54)]">Юр. адрес</div>
              <div className="w-full text-[rgba(0,0,0,0.72)]">
                {isLoading ? (
                  <PlaceholderCell className="max-w-[200px]" height={40} />
                ) : (
                  (data && data.legalAddress) || '—'
                )}
              </div>
            </div>
            {
              /* eslint-disable-next-line */
              Boolean(data?.access.viewContact) &&
                (isAttachedContactsLoading ? (
                  <PlaceholderCell height={120} />
                ) : (
                  <CustomerContactsTable
                    attachContactByCustomerId={attachContactByCustomerId}
                    attachedContacts={attachedContacts}
                    contacts={contacts}
                    customerId={id || ''}
                    detachContactByCustomerId={detachContactByCustomerId}
                    handleSearchContact={handleSearchContact}
                    isAccessAddContact={Boolean(data?.access.addContact)}
                    isAccessDeleteContact={Boolean(data?.access.deleteContact)}
                    isAttachedContactsFetching={isAttachedContactsFetching}
                    isAttachedContactsLoading={isAttachedContactsLoading}
                    isContactListFetching={isContactListFetching}
                    isContactListLoading={isContactListLoading}
                    refetchAttachedContacts={refetchAttachedContacts}
                    refetchContacts={refetchContacts}
                    searchQuery={searchQuery}
                  />
                ))
            }
          </div>
        </ModalSimple>
      </ModalItem>
    </>
  )
}

export default CustomerDetail
